/** Hotspots (UX-SPEC §8): every flagged stretch, ranked as the engine ranks them, virtualised, filterable. */
import { useLayoutEffect, useMemo, useRef } from 'preact/hooks';
import { CHANNEL_IDS } from '../../engine/channels/types.ts';
import type { ChannelId } from '../../engine/channels/types.ts';
import type { Hotspot } from '../../engine/ensemble/hotspots.ts';
import type { ViewModel } from '../../shared/viewmodel.ts';
import { HotspotAction } from '../bench/Actions.tsx';
import { Term } from '../components/Term.tsx';
import { fmt1, fmtInt, fmtP, fmtSig } from '../format.ts';
import { isChannelId, stretchLabel } from '../model.ts';
import { hashFor, navigate, route, setParams } from '../router.ts';
import { HOTSPOT_EXPLAIN, SCORE_NOTE } from '../strings-explain.ts';
import { HOTSPOTS as H, OVERVIEW } from '../strings.ts';
import { ROW_H, useMobile, useWindow } from '../ui.tsx';

const KINDS = ['segment', 'spike', 'unit'] as const;
const FLAGS = ['hotspot', 'elevated'] as const;
const CARD_H = 132;

interface Keys {
	channel: Uint8Array;
	kind: Uint8Array;
	flag: Uint8Array;
	chapter: Int16Array;
}

const keyMemo = new WeakMap<ViewModel, Keys>();

function keysOf(vm: ViewModel): Keys {
	const hit = keyMemo.get(vm);
	if (hit) return hit;
	const list = vm.ensemble.hotspots.hotspots;
	const k: Keys = {
		channel: new Uint8Array(list.length),
		kind: new Uint8Array(list.length),
		flag: new Uint8Array(list.length),
		chapter: new Int16Array(list.length),
	};
	list.forEach((h, i) => {
		k.channel[i] = CHANNEL_IDS.indexOf(h.channel);
		k.kind[i] = KINDS.indexOf(h.kind);
		k.flag[i] = FLAGS.indexOf(h.flag);
		k.chapter[i] = vm.paras.chapter[Math.min(h.paraStart, vm.paras.count - 1)] ?? -1;
	});
	keyMemo.set(vm, k);
	return k;
}

interface Filters {
	channels: ChannelId[];
	kind: number;
	flag: number;
	chapter: number;
	group: boolean;
	sort: 'strength' | 'position';
}

function filtersFrom(params: URLSearchParams): Filters {
	const chs = (params.get('ch') ?? '').split(',').filter(isChannelId);
	const kind = KINDS.indexOf((params.get('kind') ?? '') as (typeof KINDS)[number]);
	const flag = FLAGS.indexOf((params.get('flag') ?? '') as (typeof FLAGS)[number]);
	const chapter = params.has('chapter') ? Number(params.get('chapter')) : -2;
	return {
		channels: chs,
		kind,
		flag,
		chapter: Number.isFinite(chapter) ? chapter : -2,
		group: params.get('group') === '1',
		sort: params.get('sort') === 'position' ? 'position' : 'strength',
	};
}

/** Indices of the entries that pass the filters, in the requested order. Typed-array scans: well under 50 ms at 11,000 entries. */
export function selectHotspots(vm: ViewModel, f: Filters): Uint32Array {
	const list = vm.ensemble.hotspots.hotspots;
	const k = keysOf(vm);
	const want = f.channels.length ? new Set(f.channels.map((c) => CHANNEL_IDS.indexOf(c))) : null;
	const out = new Uint32Array(list.length);
	let n = 0;
	for (let i = 0; i < list.length; i++) {
		if (want && !want.has(k.channel[i] as number)) continue;
		if (f.kind >= 0 && k.kind[i] !== f.kind) continue;
		if (f.flag >= 0 && k.flag[i] !== f.flag) continue;
		if (f.chapter !== -2 && k.chapter[i] !== f.chapter) continue;
		out[n++] = i;
	}
	const sel = out.subarray(0, n);
	if (f.sort === 'position')
		sel.sort((a, b) => (list[a] as Hotspot).paraStart - (list[b] as Hotspot).paraStart || a - b);
	return sel;
}

type Row = { t: 'group'; chapter: number; count: number } | { t: 'hot'; i: number };

function rowsFor(vm: ViewModel, sel: Uint32Array, group: boolean): Row[] {
	if (!group) return Array.from(sel, (i) => ({ t: 'hot', i }) as Row);
	const k = keysOf(vm);
	const by = new Map<number, number[]>();
	for (const i of sel) {
		const c = k.chapter[i] as number;
		const l = by.get(c);
		if (l) l.push(i);
		else by.set(c, [i]);
	}
	const out: Row[] = [];
	for (const c of [...by.keys()].sort((a, b) => a - b)) {
		const l = by.get(c) as number[];
		out.push({ t: 'group', chapter: c, count: l.length });
		for (const i of l) out.push({ t: 'hot', i });
	}
	return out;
}

function countsLine(vm: ViewModel, sel: Uint32Array): string {
	const k = keysOf(vm);
	const c = new Map<string, number>();
	for (const i of sel) {
		const key = `${FLAGS[k.flag[i] as number]}-${KINDS[k.kind[i] as number]}`;
		c.set(key, (c.get(key) ?? 0) + 1);
	}
	const f = (key: string) => fmtInt(c.get(key) ?? 0);
	const parts: Array<[string, string]> = [
		['hotspot-segment', H.part.hotspotSegment(f('hotspot-segment'))],
		['elevated-segment', H.part.elevatedSegment(f('elevated-segment'))],
		['hotspot-unit', H.part.hotspotUnit(f('hotspot-unit'))],
		['elevated-unit', H.part.elevatedUnit(f('elevated-unit'))],
		['hotspot-spike', H.part.hotspotSpike(f('hotspot-spike'))],
		['elevated-spike', H.part.elevatedSpike(f('elevated-spike'))],
	];
	return H.counts(
		fmtInt(sel.length),
		parts
			.filter(([key]) => (c.get(key) ?? 0) > 0)
			.map(([, text]) => text)
			.join(', ')
	);
}

const scroll = new Map<string, number>();

function kindLabel(kind: Hotspot['kind']): string {
	return kind === 'segment' ? H.kindSegment : kind === 'spike' ? H.kindSpike : H.kindUnit;
}

/** Renders only while the route is the hotspot list, so leaving it never re-renders the list with another route. */
export function HotspotsView({ vm }: { vm: ViewModel }) {
	if (route.value.view !== 'hotspots') return null;
	return <HotspotList vm={vm} />;
}

function HotspotList({ vm }: { vm: ViewModel }) {
	const r = route.value;
	const f = filtersFrom(r.params);
	const mobile = useMobile();
	const list = vm.ensemble.hotspots.hotspots;
	const paramKey = r.params.toString();
	// biome-ignore lint/correctness/useExhaustiveDependencies: `f` is derived from `paramKey`
	const sel = useMemo(() => selectHotspots(vm, f), [vm, paramKey]);
	const rows = useMemo(() => rowsFor(vm, sel, f.group), [vm, sel, f.group]);
	const rowH = mobile ? CARD_H : ROW_H;
	const win = useWindow(rows.length, rowH, 10);
	const withheld = vm.ensemble.hotspots.withheld;
	// biome-ignore lint/correctness/useExhaustiveDependencies: `list` is part of `vm`
	const channelsWithEntries = useMemo(() => {
		const k = keysOf(vm);
		const seen = new Set<number>();
		for (let i = 0; i < list.length; i++) seen.add(k.channel[i] as number);
		return CHANNEL_IDS.filter((_, ci) => seen.has(ci));
	}, [vm]);
	const hash = location.hash;
	const hashRef = useRef(hash);
	hashRef.current = hash;
	// restore the scroll position remembered for this exact route (filters included)
	// biome-ignore lint/correctness/useExhaustiveDependencies: once per route
	useLayoutEffect(() => {
		const el = win.ref.current;
		if (!el) return;
		const saved = scroll.get(hash) ?? 0;
		el.scrollTop = saved;
		const frame = requestAnimationFrame(() => {
			if (saved > 0 && el.scrollTop !== saved) el.scrollTop = saved;
		});
		return () => cancelAnimationFrame(frame);
	}, [hash]);
	// Remember it as the list scrolls. Firefox and Safari report a scrollTop of 0 while the list is being replaced, so
	// the position is also saved on the click that leaves (a link or a row) and later writes are ignored.
	// biome-ignore lint/correctness/useExhaustiveDependencies: one listener per mount; the key is read through a ref
	useLayoutEffect(() => {
		const el = win.ref.current;
		if (!el) return;
		let leaving = false;
		const save = () => {
			if (!leaving) scroll.set(hashRef.current, el.scrollTop);
		};
		const onClick = (e: MouseEvent) => {
			if (!(e.target as Element).closest('a[href], tr.hot-row')) return;
			save();
			leaving = true;
		};
		el.addEventListener('scroll', save, { passive: true });
		document.addEventListener('click', onClick, true);
		return () => {
			el.removeEventListener('scroll', save);
			document.removeEventListener('click', onClick, true);
		};
	}, [rows.length > 0]);
	const set = (p: Record<string, string | undefined>) => setParams(p);
	const toggleChannel = (id: ChannelId) => {
		const cur = new Set(f.channels);
		if (cur.has(id)) cur.delete(id);
		else cur.add(id);
		set({ ch: [...cur].join(',') || undefined });
	};
	const open = (h: Hotspot) =>
		navigate(
			hashFor('read', {
				params: { p: String(h.paraStart), ch: h.channel, hs: `${h.paraStart}-${h.paraEnd}` },
			})
		);
	const active = f.channels.length > 0 || f.kind >= 0 || f.flag >= 0 || f.chapter !== -2;
	return (
		<div class="wrap wrap-wide" data-testid="hotspots">
			<div class="sec-head">
				<h1>{H.title}</h1>
			</div>
			<p class="caption">
				{OVERVIEW.hotspotDef}. {OVERVIEW.elevatedDef}.
			</p>
			<p class="caption">{HOTSPOT_EXPLAIN.rowMeaning}</p>
			<p class="caption" data-testid="hot-score-note">
				{SCORE_NOTE.lead}
				<Term k="evidence-strength">{SCORE_NOTE.term}</Term>
				{SCORE_NOTE.entry}
			</p>
			{withheld.length > 0 ? (
				<p class="note-notice" data-testid="hot-withheld">
					{H.notListed(
						withheld.map((w) => w.id).join(', '),
						(withheld[0]?.reason ?? '').replace(/\.$/, '')
					)}
				</p>
			) : null}
			<p data-testid="hot-counts">{countsLine(vm, sel)}</p>
			<p class="dim">{HOTSPOT_EXPLAIN.counts}</p>
			<fieldset class="filters">
				<legend class="visually-hidden">{H.filters}</legend>
				<details class="filter-channels">
					<summary>
						{H.channel}
						{f.channels.length ? ` (${f.channels.length})` : ''}
					</summary>
					<div class="filter-list" data-testid="hot-channel-filter">
						{channelsWithEntries.map((id) => (
							<label key={id} class="check-label">
								<input
									type="checkbox"
									class="check"
									checked={f.channels.includes(id)}
									onChange={() => toggleChannel(id)}
								/>
								<span class="mono">{id}</span>
							</label>
						))}
					</div>
				</details>
				<label class="sel">
					{H.kind}
					<select
						value={f.kind >= 0 ? (KINDS[f.kind] as string) : ''}
						onChange={(e) =>
							set({ kind: (e.currentTarget as HTMLSelectElement).value || undefined })
						}
					>
						<option value="">{H.all}</option>
						<option value="segment">{H.kindSegment}</option>
						<option value="spike">{H.kindSpike}</option>
						<option value="unit">{H.kindUnit}</option>
					</select>
				</label>
				<label class="sel">
					{H.flag}
					<select
						value={f.flag >= 0 ? (FLAGS[f.flag] as string) : ''}
						onChange={(e) =>
							set({ flag: (e.currentTarget as HTMLSelectElement).value || undefined })
						}
					>
						<option value="">{H.all}</option>
						<option value="hotspot">{OVERVIEW.hotspotWord}</option>
						<option value="elevated">{OVERVIEW.elevatedWord}</option>
					</select>
				</label>
				<label class="sel">
					{H.chapter}
					<select
						value={f.chapter === -2 ? '' : String(f.chapter)}
						onChange={(e) =>
							set({ chapter: (e.currentTarget as HTMLSelectElement).value || undefined })
						}
					>
						<option value="">{H.all}</option>
						{vm.chapters.map((c, i) => (
							<option key={c.charStart} value={String(i)}>
								{c.title ?? `Chapter ${i + 1}`}
							</option>
						))}
					</select>
				</label>
				<label class="check-label">
					<input
						type="checkbox"
						class="check"
						checked={f.group}
						onChange={() => set({ group: f.group ? undefined : '1' })}
					/>
					{H.groupByChapter}
				</label>
				<label class="sel">
					Order
					<select
						value={f.sort}
						onChange={(e) =>
							set({
								sort:
									(e.currentTarget as HTMLSelectElement).value === 'position'
										? 'position'
										: undefined,
							})
						}
					>
						<option value="strength">Strength (engine ranking)</option>
						<option value="position">Position in the document</option>
					</select>
				</label>
				<Term k="engine-ranking">{HOTSPOT_EXPLAIN.orderTerm}</Term>
				{active ? (
					<button
						type="button"
						class="btn-text"
						onClick={() =>
							set({ ch: undefined, kind: undefined, flag: undefined, chapter: undefined })
						}
					>
						{H.clear}
					</button>
				) : null}
			</fieldset>
			<p class="dim" aria-live="polite" data-testid="hot-shown">
				{H.shown(fmtInt(sel.length))}
			</p>
			{rows.length === 0 ? (
				<p data-testid="hot-empty">{H.noEntries}</p>
			) : (
				<section
					class={`tablewrap hot-wrap${mobile ? ' cards' : ''}`}
					ref={win.ref as { current: HTMLElement | null }}
					// a scrollable region must be keyboard reachable
					// biome-ignore lint/a11y/noNoninteractiveTabindex: scroll container of the virtualised list
					tabIndex={0}
					aria-label={H.title}
					data-testid="hot-list"
				>
					<table class="is-virtual hot-table" aria-rowcount={rows.length + 1}>
						<caption class="visually-hidden">{H.title}</caption>
						<thead>
							<tr>
								<th scope="col">{H.cols.channel}</th>
								<th scope="col">
									<Term k="segment">{H.cols.kind}</Term>
								</th>
								<th scope="col">
									<Term k="hotspot">{H.cols.flag}</Term>
								</th>
								<th scope="col">{H.cols.location}</th>
								<th scope="col" class="num">
									{H.cols.score}
								</th>
								<th scope="col" class="num">
									<Term k="rate-ratio-reference">{H.cols.ratioRef}</Term>
								</th>
								<th scope="col" class="num">
									<Term k="rate-ratio-background">{H.cols.ratioBg}</Term>
								</th>
								<th scope="col" class="num">
									<Term k="p-reference">{HOTSPOT_EXPLAIN.pHeader}</Term>
								</th>
							</tr>
						</thead>
						<tbody>
							{win.before > 0 ? (
								<tr class="spacer" ref={(el) => el?.style.setProperty('height', `${win.before}px`)}>
									<td colSpan={8} aria-hidden="true" />
								</tr>
							) : null}
							{rows.slice(win.start, win.end).map((row, k) => {
								const at = win.start + k;
								if (row.t === 'group') {
									const c = vm.chapters[row.chapter];
									return (
										<tr key={`g${row.chapter}`} class="grouprow" aria-rowindex={at + 2}>
											<th scope="colgroup" colSpan={8}>
												{row.chapter < 0 ? H.noChapter : (c?.title ?? `Chapter ${row.chapter + 1}`)}{' '}
												· {H.chapterCount(fmtInt(row.count))}
											</th>
										</tr>
									);
								}
								const h = list[row.i] as Hotspot;
								const where = stretchLabel(vm, h.paraStart, h.paraEnd);
								return (
									<tr
										key={row.i}
										class="hot-row"
										aria-rowindex={at + 2}
										data-channel={h.channel}
										data-flag={h.flag}
										data-kind={h.kind}
										onClick={(e) => {
											if ((e.target as HTMLElement).closest('a')) return;
											open(h);
										}}
										onKeyDown={(e) => {
											if (e.key === 'Enter' && e.target === e.currentTarget) open(h);
										}}
									>
										<th scope="row" data-label={H.cols.channel}>
											<a
												href={hashFor('read', {
													params: {
														p: String(h.paraStart),
														ch: h.channel,
														hs: `${h.paraStart}-${h.paraEnd}`,
													},
												})}
												aria-label={H.rowLabel(h.channel, where)}
											>
												<span class="mono">{h.channel}</span> {h.name}
											</a>
										</th>
										<td data-label={H.cols.kind}>{kindLabel(h.kind)}</td>
										<td data-label={H.cols.flag}>
											{h.flag === 'hotspot' ? OVERVIEW.hotspotWord : OVERVIEW.elevatedWord}
										</td>
										<td data-label={H.cols.location}>
											{where}
											<span class="hot-chevron" aria-hidden="true">
												{' ›'}
											</span>
											{h.notes?.map((n) => (
												<span key={n} class="note-notice">
													{' '}
													{n}
												</span>
											))}
											<HotspotAction
												channel={h.channel}
												paraStart={h.paraStart}
												paraEnd={h.paraEnd}
											/>
										</td>
										<td class="num" data-label={H.cols.score}>
											{fmt1(h.score)}{' '}
											<span class="dim">
												({fmtSig(h.ci90[0])}–{fmtSig(h.ci90[1])})
											</span>
										</td>
										<td class="num" data-label={H.cols.ratioRef}>
											{h.rateRatioRef === undefined ? '—' : fmtSig(h.rateRatioRef)}
										</td>
										<td class="num" data-label={H.cols.ratioBg}>
											{h.rateRatioBg === undefined ? '—' : fmtSig(h.rateRatioBg)}
										</td>
										<td class="num" data-label={H.cols.p}>
											{fmtP(h.pRef)}
											{h.pBg === undefined ? '' : ` / ${fmtP(h.pBg)}`}
										</td>
									</tr>
								);
							})}
							{win.after > 0 ? (
								<tr class="spacer" ref={(el) => el?.style.setProperty('height', `${win.after}px`)}>
									<td colSpan={8} aria-hidden="true" />
								</tr>
							) : null}
						</tbody>
					</table>
				</section>
			)}
		</div>
	);
}
